import { ArrowUp, Download, Eye, FileText, Folder, Trash2, TriangleAlert, X } from 'lucide-react';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import notify from '@/lib/notify';
import { MinioItem } from '@/types';
import documentService from '@/services/documentService';
import DocumentUpload from '@/components/Documents/DocumentUpload';
import { DocumentVisibilityControl } from '@/components/Documents/KnowledgeVisibility';
import type { DocumentsContext } from '@/hooks/useDocuments';
import { useDocuments } from '@/hooks/useDocuments';

export interface DocumentManagerSectionProps {
  context: DocumentsContext | undefined;
  /** Optional: when provided, show Genie Importer button and call this when clicked */
  onImportToAIExtraction?: (item: MinioItem) => void;
  isReadOnly?: boolean;
  /** Optional: max height for the list area (default allows scroll) */
  className?: string;
}

const PREVIEWABLE_MIMES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/html',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

function isPreviewable(item: MinioItem): boolean {
  const mime = (item as MinioItem & { mimeType?: string }).mimeType?.toLowerCase();
  if (mime && PREVIEWABLE_MIMES.some((p) => mime.startsWith(p.split('/')[0]) || mime === p)) return true;
  const name = item.name.toLowerCase();
  return ['.pdf', '.txt', '.csv', '.html', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].some((ext) =>
    name.endsWith(ext)
  );
}

export const DocumentManagerSection: React.FC<DocumentManagerSectionProps> = ({
  context,
  onImportToAIExtraction,
  isReadOnly = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const docs = useDocuments(context);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [previewItem, setPreviewItem] = useState<MinioItem | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewBlobUrlRef = useRef<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'file' | 'folder'; idOrPath: string; name: string } | null>(
    null
  );
  const [uploadProgress, setUploadProgress] = useState<{ total: number; completed: number }>({ total: 0, completed: 0 });

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setUploadProgress({ total: files.length, completed: 0 });
      const { successCount, errorCount } = await docs.uploadFiles(files, (completed, total) => {
        setUploadProgress({ completed, total });
      });

      if (successCount > 0) {
        notify.success(t('documents.manager.uploadSuccess', { count: successCount }));
      }
      if (errorCount > 0) {
        notify.error(
          docs.error
          || t('cadrage.documents.uploadError', {
            error: t('documents.manager.filesCount', { count: errorCount }),
          })
        );
      }

      setUploadProgress((prev) => ({ ...prev, completed: prev.total }));
    },
    [docs, t]
  );

  const handleGoUp = () => {
    if (docs.selectedFolderPath === '/') return;
    const parts = docs.selectedFolderPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = '/' + parts.join('/') + (parts.length > 0 ? '/' : '');
    docs.setSelectedFolderPath(newPath);
  };

  const handleFolderClick = (folderName: string) => {
    const newPath = docs.selectedFolderPath.endsWith('/')
      ? docs.selectedFolderPath + folderName
      : docs.selectedFolderPath + '/' + folderName;
    docs.setSelectedFolderPath(newPath);
  };

  const handleCreateFolder = useCallback(async () => {
    const folderName = window.prompt(t('cadrage.documents.promptFolderName'));
    if (!folderName?.trim()) {
      if (folderName !== null) notify.error(t('cadrage.documents.folderNameEmpty'));
      return;
    }
    const cleaned = folderName.trim().replace(/\s+/g, '-') + '/';
    setIsCreatingFolder(true);
    try {
      await docs.createFolder(cleaned);
      notify.success(t('cadrage.documents.folderCreated', { name: folderName.trim() }));
    } catch (err) {
      notify.error(
        t('cadrage.documents.folderCreateError', {
          error: err instanceof Error ? err.message : String(err),
        })
      );
    } finally {
      setIsCreatingFolder(false);
    }
  }, [docs, t]);

  const handleDownload = useCallback(
    async (item: MinioItem) => {
      const path = item.documentId
        ? documentService.getStreamPathByDocumentId(item.documentId, 'attachment')
        : item.minioPath
          ? documentService.getStreamPath(item.minioPath, 'attachment')
          : null;
      if (!path) return;
      setDownloadingId(item.documentId ?? item.name);
      try {
        const blob = await documentService.downloadDocument(path);
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = item.name.replace(/\/$/, '');
        a.click();
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        notify.error(t('domainDetail.documents.downloadError') || 'Download failed');
      } finally {
        setDownloadingId(null);
      }
    },
    [t]
  );

  const displayPath = docs.selectedFolderPath === '/' ? t('cadrage.documents.root') : docs.selectedFolderPath;
  const filePath = (name: string) =>
    docs.selectedFolderPath === '/' ? `/${name}` : `${docs.selectedFolderPath}${name}`;
  const uploadPercent = uploadProgress.total > 0 ? Math.round((uploadProgress.completed / uploadProgress.total) * 100) : 0;

  // Fetch preview blob when preview modal opens; revoke on close
  useEffect(() => {
    if (!previewItem) {
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
      setPreviewBlobUrl(null);
      setPreviewError(null);
      return;
    }
    const path = previewItem.documentId
      ? documentService.getStreamPathByDocumentId(previewItem.documentId, 'inline')
      : previewItem.minioPath
        ? documentService.getStreamPath(previewItem.minioPath, 'inline')
        : null;
    if (!path) {
      setPreviewError(t('cadrage.documents.previewNotAvailable'));
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    documentService
      .downloadDocument(path)
      .then((blob) => {
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          previewBlobUrlRef.current = url;
          setPreviewBlobUrl(url);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setPreviewError(err?.message || t('cadrage.documents.previewNotAvailable'));
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
    };
  }, [previewItem?.documentId, previewItem?.minioPath, t]);

  const closePreview = useCallback(() => {
    if (previewBlobUrlRef.current) {
      URL.revokeObjectURL(previewBlobUrlRef.current);
      previewBlobUrlRef.current = null;
    }
    setPreviewBlobUrl(null);
    setPreviewItem(null);
    setPreviewError(null);
  }, []);

  return (
    <div className={`bg-card shadow rounded-lg p-6 space-y-4 overflow-y-auto max-h-[calc(100vh-200px)] ${className}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between border-b border-border pb-3 mb-4">
        <div className="flex items-center gap-2">
          {docs.selectedFolderPath !== '/' && (
            <button
              type="button"
              onClick={handleGoUp}
              className="p-1 text-muted-foreground hover:text-foreground hover:bg-accent rounded"
              title={t('cadrage.documents.goUp')}
            >
              <ArrowUp className="h-5 w-5" />
            </button>
          )}
          <h2 className="text-xl font-semibold text-foreground truncate">
            {t('cadrage.documents.title')}: {displayPath}
          </h2>
        </div>
        {!isReadOnly && (
          <div className="mt-2 sm:mt-0 flex items-center gap-2">
            {docs.supportsFolderActions && (
              <button
                type="button"
                onClick={handleCreateFolder}
                disabled={isCreatingFolder || docs.isLoading}
                className="px-3 py-2 bg-secondary text-secondary-foreground rounded-md shadow hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 flex items-center gap-1 text-sm"
              >
                <Folder className="h-4 w-4" />
                {isCreatingFolder ? t('global.creating') : t('cadrage.documents.newFolder')}
              </button>
            )}
            <DocumentUpload onFilesSelect={handleFilesSelected} />
          </div>
        )}
      </div>
      {docs.isLoading && uploadProgress.total > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t('documents.explorer.uploading')}</span>
            <span>{uploadProgress.completed}/{uploadProgress.total}</span>
          </div>
          <div className="h-2 w-full rounded bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all duration-200" style={{ width: `${uploadPercent}%` }} />
          </div>
        </div>
      )}

      {docs.isLoading && (
        <p className="text-center text-muted-foreground italic py-6">{t('cadrage.documents.loading')}</p>
      )}

      {docs.error && (
        <div
          className="bg-red-100 dark:bg-red-900/20 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded flex items-center gap-2"
          role="alert"
        >
          <TriangleAlert className="h-5 w-5 flex-shrink-0" />
          <span>{docs.error}</span>
        </div>
      )}

      {!docs.isLoading && !docs.error && (
        <>
          {docs.folderContent.length === 0 ? (
            <p className="text-center text-muted-foreground italic py-6">{t('cadrage.documents.emptyFolder')}</p>
          ) : (
            <ul className="space-y-2">
              {docs.folderContent.map((item) => (
                <li
                  key={item.type === 'folder' ? `folder-${item.name}` : `file-${item.documentId ?? item.name}`}
                  className={`flex items-center justify-between gap-2 p-2 rounded border border-border/50 ${
                    item.type === 'folder' ? 'hover:bg-accent/50 cursor-pointer' : 'hover:bg-accent/30'
                  }`}
                  onClick={item.type === 'folder' ? () => handleFolderClick(item.name) : undefined}
                >
                  <div className="flex items-center gap-3 truncate min-w-0 flex-1">
                    {item.type === 'folder' ? (
                      <Folder className="h-5 w-5 text-primary flex-shrink-0" />
                    ) : (
                      <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-foreground truncate block">
                        {item.name.replace(/\/$/, '')}
                      </span>
                      <span className="text-xs text-muted-foreground truncate block">
                        {t('cadrage.documents.path', 'Path')}: {filePath(item.name.replace(/\/$/, ''))}
                        {item.type === 'file' &&
                          (item as MinioItem & { mimeType?: string }).mimeType &&
                          ` · ${(item as MinioItem & { mimeType?: string }).mimeType}`}
                      </span>
                      {item.type === 'file' && item.documentId && (
                        <DocumentVisibilityControl
                          className="mt-1"
                          documentId={item.documentId}
                          visibility={item.visibility}
                          ownerId={item.ownerId}
                          readOnly={isReadOnly}
                          onVisibilityChange={docs.setItemVisibility}
                        />
                      )}
                    </div>
                  </div>

                  {item.type === 'file' && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {(item as MinioItem & { mimeType?: string }).mimeType && (
                        <span className="text-xs text-muted-foreground hidden sm:inline">
                          {(item as MinioItem & { mimeType?: string }).mimeType}
                        </span>
                      )}
                      {isPreviewable(item) && (item.documentId || item.minioPath) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewItem(item);
                          }}
                          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded"
                          title={t('cadrage.documents.showPreview', 'Show')}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.documentId || item.minioPath) handleDownload(item);
                        }}
                        disabled={!item.minioPath || downloadingId === (item.documentId ?? item.name)}
                        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded"
                        title={t('domainDetail.documents.download', { name: item.name })}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {onImportToAIExtraction && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onImportToAIExtraction(item);
                          }}
                          className="text-xs border border-primary/60 text-primary hover:bg-primary/10 px-2 py-1 rounded"
                          title={t('cadrage.documents.useGenieImporter', { name: item.name })}
                        >
                          {t('cadrage.documents.genieImporter')}
                        </button>
                      )}
                      {!isReadOnly && item.documentId && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete({ type: 'file', idOrPath: item.documentId!, name: item.name });
                          }}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-500/10 rounded"
                          title={t('cadrage.documents.deleteFileTitle', { name: item.name })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {item.type === 'folder' && !isReadOnly && docs.supportsFolderActions && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete({
                          type: 'folder',
                          idOrPath: item.name,
                          name: item.name.replace(/\/$/, ''),
                        });
                      }}
                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-500/10 rounded flex-shrink-0"
                      title={t('cadrage.documents.deleteFolderTitle', { name: item.name.replace(/\/$/, '') })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-card rounded-lg shadow-lg border border-border w-full max-w-md mx-4">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h4 className="text-md font-medium text-foreground">
                {t('domainDetail.documents.confirmDeleteTitle')}
              </h4>
              <button
                type="button"
                aria-label={t('global.close')}
                onClick={() => setConfirmDelete(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-foreground">
                {t('domainDetail.documents.confirmDeleteMessage', { name: confirmDelete.name })}
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="text-sm px-3 py-1 border border-border rounded hover:bg-muted"
                  onClick={() => setConfirmDelete(null)}
                >
                  {t('domainDetail.documents.cancel')}
                </button>
                <button
                  type="button"
                  className="text-sm px-3 py-1 border border-destructive text-destructive rounded hover:bg-destructive/10"
                  onClick={async () => {
                    if (!confirmDelete) return;
                    try {
                      if (confirmDelete.type === 'file') {
                        await docs.deleteDocument(confirmDelete.idOrPath);
                      } else {
                        const path = confirmDelete.idOrPath.endsWith('/') ? confirmDelete.idOrPath : confirmDelete.idOrPath + '/';
                        await docs.deleteFolder(path);
                      }
                      setConfirmDelete(null);
                    } catch {
                      // Error already notified in handler
                    }
                  }}
                >
                  {t('domainDetail.documents.confirmDelete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewItem && (previewItem.documentId || previewItem.minioPath) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="absolute inset-4 flex flex-col rounded-lg border border-border bg-card shadow-xl overflow-hidden">
            <div className="flex items-center justify-between p-2 border-b border-border bg-muted/50">
              <span className="text-sm font-medium text-foreground truncate">{previewItem.name}</span>
              <button
                type="button"
                onClick={closePreview}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded"
                aria-label={t('global.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-2">
              {previewLoading && (
                <p className="text-center text-muted-foreground py-12">{t('cadrage.documents.loading')}</p>
              )}
              {previewError && (
                <p className="text-center text-destructive py-12">{previewError}</p>
              )}
              {!previewLoading && !previewError && previewBlobUrl && (() => {
                const mime = (previewItem as MinioItem & { mimeType?: string }).mimeType?.toLowerCase() ?? '';
                const name = previewItem.name.toLowerCase();
                const isPdf =
                  mime === 'application/pdf' || name.endsWith('.pdf');
                const isImage =
                  mime.startsWith('image/') ||
                  ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].some((e) => name.endsWith(e));
                const isText =
                  mime.startsWith('text/') || ['.txt', '.csv', '.html'].some((e) => name.endsWith(e));

                if (isPdf) {
                  return (
                    <iframe
                      src={previewBlobUrl}
                      title={previewItem.name}
                      className="w-full h-full min-h-[70vh] rounded border-0"
                    />
                  );
                }
                if (isImage) {
                  return (
                    <img
                      src={previewBlobUrl}
                      alt={previewItem.name}
                      className="max-w-full max-h-[80vh] object-contain mx-auto"
                    />
                  );
                }
                if (isText) {
                  return (
                    <iframe
                      src={previewBlobUrl}
                      title={previewItem.name}
                      className="w-full h-full min-h-[70vh] rounded border border-border"
                    />
                  );
                }
                return (
                  <p className="text-muted-foreground text-center py-8">
                    {t('cadrage.documents.previewNotAvailable', 'Preview not available. Use Download.')}
                  </p>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagerSection;
